import {
  ConditionalOperatorArray,
  ConditionalOperatorPrimitive,
  FormFieldSchemaType,
  SchemaDentalTreatmentType,
  SchemaEngineCapacity,
  SchemaIncidentLocation,
  SchemaIncidentMainCause,
  SchemaIncidentTiming,
  SchemaInjuryCause,
  SchemaInjuryType,
  SchemaPersonalInvolvement,
  SchemaRadioYesOrNo,
  SchemaVehicleType,
  type FormStepSchema,
  type SelectFieldOption,
} from '@/types';
import yesOrNoOptions from '@data-shared/constants/yesOrNoOptions';
import { medicalConditionName } from '../medicalConditionName';
import { schemaFieldName } from '../schemaFieldName';
import { schemaStepName } from '../schemaStepName';

interface GenerateNewClaimStepWhatHappenedFormSchemaParams {
  incidentTiming?: SchemaIncidentTiming;
}

const { incident } = schemaFieldName;
const {
  illnessDetails,
  dentalDetails,
  injuryDetails,
  emergencyAssistanceDetails,
  hospitalisationDetails,
} = incident;
const { vehicleSpecificInjuryDetails, snowSportsSpecificInjuryDetails } = injuryDetails;

let stepWhatHappenedFormSchema: FormStepSchema = {
  name: schemaStepName.stepWhatHappened,
  title: 'Tell us more about what happened',
  titleShort: 'What happened',
  lastUpdated: Date.now(),
  form: {
    name: `${schemaStepName.stepWhatHappened}Form`,
    fields: [],
  },
};

const medicalConditionOptions: SelectFieldOption<string>[] = [
  {
    value: medicalConditionName.acuteRespiratory,
    label: 'Acute respiratory',
    description: '(e.g. COVID-19, pneumonia, influenza, or other viral infection or illness)',
  },
  {
    value: medicalConditionName.altitudeSickness,
    label: 'Altitude sickness',
  },
  {
    value: medicalConditionName.arthritisOrRheumatologicalIllness,
    label: 'Arthritis or rheumatological illness',
    description: '(e.g. lupus, tendonitis, gout, knee or hip replacement)',
  },
  {
    value: medicalConditionName.asthma,
    label: 'Asthma',
  },
  {
    value: medicalConditionName.autoimmuneIllness,
    label: 'Autoimmune illness',
    description: '(e.g. related to immunology.)',
  },
  {
    value: medicalConditionName.cancer,
    label: 'Cancer',
    description:
      '(e.g. breast cancer, kidney cancer, leukemia, liver cancer, melanoma, non-Hodgkins lymphoma, or skin cancer)',
  },
  {
    value: medicalConditionName.cardiovascularDisease,
    label: 'Cardiovascular disease',
    description:
      '(e.g. angina, arrhythmia, atrial fibrillation, cardiomyopathy, heart attacks, heart disease, heart failure, heart valve conditions, high blood pressure, high cholesterol, or pacemakers)',
  },
  {
    value: medicalConditionName.chronicLungConditions,
    label: 'Chronic lung conditions',
    description: '(e.g. emphysema)',
  },
  {
    value: medicalConditionName.diabetes,
    label: 'Diabetes',
  },
  {
    value: medicalConditionName.earEyeNoseOrThroatIllness,
    label: 'Ear, eye, nose or throat illness',
    description: '(e.g. ear infections, eye or sinus problems)',
  },
  {
    value: medicalConditionName.gastrointestinalDisease,
    label: 'Gastrointestinal disease',
    description: "(e.g. Crohn's disease or ulcerative colitis)",
  },
  {
    value: medicalConditionName.gastrointestinalIllness,
    label: 'Gastrointestinal illness',
    description:
      '(e.g. Bali belly, diarrhoea, food poisoning, gastroenteritis, nausea, stomach cramps, or vomiting)',
  },
  {
    value: medicalConditionName.kidneyOrUrinaryConditions,
    label: 'Kidney or urinary conditions',
    description:
      '(e.g. dialysis, kidney impairment or failure, prostate conditions, renal conditions or urinary tract infections)',
  },
  {
    value: medicalConditionName.mentalHealthConditions,
    label: 'Mental health conditions',
    description:
      '(e.g. anxiety, bipolar disorder, depression, emotional distress, long term or recurring mental health conditions, panic attacks or stress)',
  },
  {
    value: medicalConditionName.muscularSkeletalConditions,
    label: 'Muscular skeletal conditions',
    description: '(e.g. back or disc problems, osteoporosis, or sciatica)',
  },
  {
    value: medicalConditionName.neurologicalConditions,
    label: 'Neurological conditions',
    description: '(e.g. epilepsy, motor neuron disease, Parkinsons disease, or seizures)',
  },
  {
    value: medicalConditionName.pregnancy,
    label: 'Pregnancy',
  },
  {
    value: medicalConditionName.rheumatology,
    label: 'Rheumatology',
  },
  {
    value: medicalConditionName.stroke,
    label: 'Stroke',
    description: '(including mini stroke)',
  },
  {
    value: medicalConditionName.other,
    label: 'Other',
  },
];

function generateNewClaimStepWhatHappenedFormSchema({
  incidentTiming = SchemaIncidentTiming.PreTrip,
}: GenerateNewClaimStepWhatHappenedFormSchemaParams = {}): FormStepSchema {
  const { form, ...otherSchemaProps } = stepWhatHappenedFormSchema;
  stepWhatHappenedFormSchema = {
    ...otherSchemaProps,
    lastUpdated: Date.now(),
    form: {
      name: form.name,
      fields: [
        // Q4.1 - Cause of the medical incident.
        {
          type: FormFieldSchemaType.Radio,
          name: incident.mainCause,
          label: 'What was the cause of the medical incident?',
          options: [
            {
              label: 'A dental emergency',
              value: SchemaIncidentMainCause.Dental,
            },
            {
              label: 'An illness',
              value: SchemaIncidentMainCause.Illness,
            },
            {
              label: 'An injury',
              value: SchemaIncidentMainCause.Injury,
            },
          ],
        },
        // Q4.1.1.1 - Dental treatment type (shown when cause is dental).
        {
          type: FormFieldSchemaType.Radio,
          name: dentalDetails.treatmentType,
          label: 'Select the type of dental treatment.',
          isVisibleWhen: [
            {
              field: incident.mainCause,
              value: SchemaIncidentMainCause.Dental,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Cosmetic dental (e.g teeth whitening or veneers)',
              value: SchemaDentalTreatmentType.Cosmetic,
            },
            {
              label: 'Emergency dental (e.g. emergency dental surgery)',
              value: SchemaDentalTreatmentType.Emergency,
            },
            {
              label: 'Routine dental (e.g. dental x-rays or flouride treatments)',
              value: SchemaDentalTreatmentType.Routine,
            },
          ],
          // Q4.1.1.2 - Highlighted text box shown with the dental question.
          information: {
            description:
              "If the medical incident required dental treatment due to an injury - please select 'injury' and include any dental expenses with the claim submission.",
            position: 'before',
          },
        },
        // Q4.1.2.1 - Injury type (shown when cause is an injury).
        {
          type: FormFieldSchemaType.Radio,
          name: injuryDetails.injuryType,
          label: 'Select the injury type.',
          isVisibleWhen: [
            {
              field: incident.mainCause,
              value: SchemaIncidentMainCause.Injury,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Broken bones or fractures',
              value: SchemaInjuryType.BrokenBonesOrFractures,
            },
            {
              label: 'Sprain or tear in muscles or ligaments',
              value: SchemaInjuryType.SprainOrTearInMusclesOrLigaments,
            },
            {
              label: 'An accident or other injury',
              value: SchemaInjuryType.AccidentOrOther,
            },
          ],
          // Q4.1.2.3 - Highlighted text box shown for any injury.
          information: {
            description: (
              <p>
                <strong>Important:</strong> You'll need to submit an incident report, police report
                or confirmation report with your claim. Our claims team will contact you if they
                need any more information on top of this.
              </p>
            ),
            position: 'before',
          },
        },
        // Q4.1.2.2 - Cause of the injury.
        {
          type: FormFieldSchemaType.Select,
          name: injuryDetails.injuryCause,
          label: 'Select what caused the injury.',
          placeholder: 'Select cause',
          isVisibleWhen: [
            {
              field: incident.mainCause,
              value: SchemaIncidentMainCause.Injury,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Accidental fall',
              value: SchemaInjuryCause.AccidentalFall,
            },
            {
              label: 'Adventure activity',
              value: SchemaInjuryCause.AdventureActivity,
            },
            {
              label: 'Assault',
              value: SchemaInjuryCause.Assault,
            },
            {
              label: 'Motor vehicle/motorcycle/moped',
              value: SchemaInjuryCause.MotorVehicle,
            },
            {
              label: 'Snow sports',
              value: SchemaInjuryCause.SnowSports,
            },
            {
              label: 'Other',
              value: SchemaInjuryCause.Other,
            },
          ],
        },
        {
          value: incidentTiming,
          name: incident.incidentTiming,
          type: FormFieldSchemaType.Select,
          label: 'Select trip status',
          isDisabled: true,
          layoutOptions: {
            isVisuallyHidden: true,
          },
          options: [
            {
              label: 'Pre-trip',
              value: SchemaIncidentTiming.PreTrip,
            },
            {
              label: 'On-trip',
              value: SchemaIncidentTiming.OnTrip,
            },
          ],
        },
        // Q4.1.2.2.1 - Location of an accidental fall.
        {
          type: FormFieldSchemaType.Select,
          name: injuryDetails.accidentalFallSpecificInjuryDetails.incidentLocation,
          label: 'Select where this incident happened.',
          placeholder: 'Select location',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.AccidentalFall,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'At my accommodation',
              value: SchemaIncidentLocation.Accommodation,
            },
            {
              label: 'At the airport',
              value: SchemaIncidentLocation.Airport,
            },
            {
              label: 'On a bus or train',
              value: SchemaIncidentLocation.TrainOrBus,
            },
            {
              label: 'On a cruise',
              value: SchemaIncidentLocation.Cruise,
            },
            {
              label: 'On a plane',
              value: SchemaIncidentLocation.Plane,
            },
            {
              label: 'In a public place',
              value: SchemaIncidentLocation.PublicPlace,
            },
            {
              label: 'Other',
              value: SchemaIncidentLocation.Other,
            },
          ],
        },
        // Q4.1.2.2.2 - Adventure activity type.
        {
          type: FormFieldSchemaType.Text,
          name: injuryDetails.adventureActivitySpecificInjuryDetails.activityType,
          label: 'Select what type of adventure activity you were participating in.',
          placeholder: 'Enter adventure activity',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.AdventureActivity,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.1.2.2.3 - Vehicle type.
        {
          type: FormFieldSchemaType.Radio,
          name: vehicleSpecificInjuryDetails.vehicleType,
          label: 'What was the type of vehicle?',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.MotorVehicle,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Car',
              value: SchemaVehicleType.Car,
            },
            {
              label: 'Motorcycle/Moped',
              value: SchemaVehicleType.MotorcycleOrMoped,
            },
            {
              label: 'Other',
              value: SchemaVehicleType.Other,
            },
          ],
        },
        // Q4.1.2.2.3.1 - Engine capacity (motorcycle/moped only).
        {
          type: FormFieldSchemaType.Radio,
          name: vehicleSpecificInjuryDetails.motorcycleMopedEngineCapacityCC,
          label: 'What was the engine capacity of the vehicle?',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: vehicleSpecificInjuryDetails.vehicleType,
              value: SchemaVehicleType.MotorcycleOrMoped,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: '250cc or more',
              value: SchemaEngineCapacity.AtLeast250cc,
            },
            {
              label: 'Less than 250cc',
              value: SchemaEngineCapacity.LessThan250cc,
            },
          ],
        },
        // Q4.1.2.2.4 - Personal involvement in the vehicle incident.
        {
          type: FormFieldSchemaType.Radio,
          name: vehicleSpecificInjuryDetails.personalInvolvement,
          label: 'Were you the driver, passenger or pedestrian?',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.MotorVehicle,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'Driver',
              value: SchemaPersonalInvolvement.Driver,
            },
            {
              label: 'Passenger',
              value: SchemaPersonalInvolvement.Passenger,
            },
            {
              label: 'Pedestrian',
              value: SchemaPersonalInvolvement.Pedestrian,
            },
          ],
        },
        // Q4.1.2.2.5 - Was another person involved?
        {
          type: FormFieldSchemaType.Radio,
          name: vehicleSpecificInjuryDetails.multiplePeopleInvolved,
          label: 'Was there another person involved in the incident?',
          layoutOptions: {
            optionsPositioning: 'inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.MotorVehicle,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: yesOrNoOptions,
        },
        // Q4.1.2.2.5.1 - Other involved person first name.
        {
          type: FormFieldSchemaType.Text,
          name: vehicleSpecificInjuryDetails.otherInvolvedPersons.firstName,
          label: 'First name',
          placeholder: 'Enter first name',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: vehicleSpecificInjuryDetails.multiplePeopleInvolved,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.1.2.2.5.2 - Other involved person last name.
        {
          type: FormFieldSchemaType.Text,
          name: vehicleSpecificInjuryDetails.otherInvolvedPersons.lastName,
          label: 'Last name',
          placeholder: 'Enter last name',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: vehicleSpecificInjuryDetails.multiplePeopleInvolved,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.1.2.2.6 - On-piste or off-piste (snow sports only).
        {
          type: FormFieldSchemaType.Radio,
          name: snowSportsSpecificInjuryDetails.activityOffPiste,
          label: 'Were you on-piste or off-piste when the incident happened?',
          description: (
            <>
              <p>
                <strong>On-piste:</strong> A marked trail or slope within the boundary of a snow
                sports field or resort.
              </p>
              <p>
                <strong>Off-piste:</strong> Anywhere outside of an on-piste snow sports location.
                This could include open fields, unmarked areas through trees, or along the edges of
                a mountain.
              </p>
            </>
          ),
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: injuryDetails.injuryCause,
              value: SchemaInjuryCause.SnowSports,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: [
            {
              label: 'On-piste',
              value: 'false',
            },
            {
              label: 'Off-piste',
              value: 'true',
            },
          ],
        },
        // Q4.1.4 - Diagnosed medical condition (shown when cause is an illness).
        {
          type: FormFieldSchemaType.Select,
          name: illnessDetails.medicalCondition,
          label: 'Select the diagnosed medical condition.',
          placeholder: 'Search medical conditions',
          isVisibleWhen: [
            {
              field: incident.mainCause,
              value: SchemaIncidentMainCause.Illness,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: medicalConditionOptions,
          // Q4.1.4.1 - Highlighted text box for conditions needing a medical form.
          information: {
            description: (
              <p>
                You need to submit a medical form when claiming for this condition. Please download
                it{' '}
                <a target="_blank" rel="noopener noreferrer" href="/files/claim_medical-form.pdf">
                  here
                </a>
                , have it completed by your doctor, and submit with your claim.
              </p>
            ),
            isVisibleWhen: [
              {
                value: [
                  medicalConditionName.acuteRespiratory,
                  medicalConditionName.altitudeSickness,
                  medicalConditionName.earEyeNoseOrThroatIllness,
                  medicalConditionName.gastrointestinalIllness,
                ],
                operator: ConditionalOperatorArray.NotIn,
              },
            ],
          },
        },
        // Q4.2 - Free text description of what happened.
        {
          type: FormFieldSchemaType.Textarea,
          name: incident.incidentDescription,
          label: 'Tell us what happened',
          placeholder: 'You can enter up to 1500 characters.',
          maxLength: 1500,
          layoutOptions: {
            hasSeparator: true,
          },
        },
        // Q4.3 - Spoken to 24/7 emergency assistance team?
        {
          type: FormFieldSchemaType.Radio,
          name: emergencyAssistanceDetails.emergencyAssistanceContacted,
          label: 'Have you spoken to our 24/7 emergency assistance team about this incident?',
          layoutOptions: {
            hasSeparator: true,
            optionsPositioning: 'inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: yesOrNoOptions,
        },
        // Q4.3.1 - Emergency assistance reference number.
        {
          type: FormFieldSchemaType.Text,
          name: emergencyAssistanceDetails.callReferenceNumber,
          label: 'Enter the emergency assistance reference',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: emergencyAssistanceDetails.emergencyAssistanceContacted,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.4 - In-patient hospital care required?
        {
          type: FormFieldSchemaType.Radio,
          name: hospitalisationDetails.hospitalAdmissionRequired,
          label: 'Did you need in-patient hospital care because of the incident?',
          layoutOptions: {
            hasSeparator: true,
            optionsPositioning: 'inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          options: yesOrNoOptions,
        },
        // Q4.4.1 - Hospital name.
        {
          type: FormFieldSchemaType.Text,
          name: hospitalisationDetails.hospitalName,
          label: 'Enter the name of the hospital',
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: hospitalisationDetails.hospitalAdmissionRequired,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.4.2 - Date of admission.
        {
          type: FormFieldSchemaType.Date,
          name: hospitalisationDetails.admissionDate,
          label: 'Enter the date of admission',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: hospitalisationDetails.hospitalAdmissionRequired,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.4.4 - Time of admission.
        {
          type: FormFieldSchemaType.Time,
          name: hospitalisationDetails.admissionTime,
          label: 'Enter the time of admission',
          placeholder: 'HH:MM AM/PM',
          layoutOptions: {
            width: '1/2-inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: hospitalisationDetails.hospitalAdmissionRequired,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
        // Q4.4.3 - Date of discharge.
        {
          type: FormFieldSchemaType.Date,
          name: hospitalisationDetails.dischargeDate,
          label: 'Enter the date of discharge',
          placeholder: 'dd/mm/yyyy',
          layoutOptions: {
            width: '1/2-inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: hospitalisationDetails.hospitalAdmissionRequired,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
          isValidWhen: [
            {
              field: hospitalisationDetails.admissionDate,
              operator: ConditionalOperatorPrimitive.GreaterThan,
              message: 'Date selected or entered must be after the admission date',
            },
          ],
        },
        // Q4.4.5 - Time of discharge.
        {
          type: FormFieldSchemaType.Time,
          name: hospitalisationDetails.dischargeTime,
          label: 'Enter the time of discharge',
          placeholder: 'HH:MM AM/PM',
          layoutOptions: {
            width: '1/2-inline',
          },
          isVisibleWhen: [
            {
              field: incident.incidentTiming,
              value: SchemaIncidentTiming.OnTrip,
              operator: ConditionalOperatorPrimitive.Equal,
            },
            {
              field: hospitalisationDetails.hospitalAdmissionRequired,
              value: SchemaRadioYesOrNo.Yes,
              operator: ConditionalOperatorPrimitive.Equal,
            },
          ],
        },
      ],
    },
  };

  return stepWhatHappenedFormSchema;
}

function getNewClaimStepWhatHappenedFormSchema() {
  if (!stepWhatHappenedFormSchema.form.fields.length) {
    // Generate a new schema with all fields, if its empty!
    stepWhatHappenedFormSchema = generateNewClaimStepWhatHappenedFormSchema();
  }

  return stepWhatHappenedFormSchema;
}

export { generateNewClaimStepWhatHappenedFormSchema, getNewClaimStepWhatHappenedFormSchema };

export type { GenerateNewClaimStepWhatHappenedFormSchemaParams };
